import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  BackendValidationError,
  CacheService,
  ErrorKeys,
  ErrorMessagesComponent,
  ErrorStateHighlightDirective,
  GbTheme,
  GoogleTagService,
  MarkFormTouchedDirective,
  otpExpiryCalculator,
  PasswordInputComponent,
  RegistrationService,
  SocialLoginButtonsComponent,
  THEME_SIGNAL,
} from '@gb-webapp/gb-app';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, finalize, map, Observable, of, Subject, throwError } from 'rxjs';

interface FieldValidationRules {
  email: { [key: string]: string };
  password: { [key: string]: string };
  referrerReferralCode: { [key: string]: string };
  signupForm: { [key: string]: string };
}

@Component({
  selector: 'app-sign-up',
  imports: [
    ReactiveFormsModule,
    SocialLoginButtonsComponent,
    RouterLink,
    PasswordInputComponent,
    ErrorStateHighlightDirective,
    MarkFormTouchedDirective,
    ErrorMessagesComponent,
    TranslatePipe,
  ],
  templateUrl: './sign-up.component.html',
  styleUrl: './sign-up.component.scss',
})
export class SignUpComponent {
  public autoRedirectOnSocialLogin = input<boolean>(false);

  protected theme = inject(THEME_SIGNAL);

  public GbTheme = GbTheme;

  public postAuthRedirect = input<{ url?: string; queryParams?: { [key: string]: string } }>({});

  signupForm = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.email,
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      ],
    }),
    password: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/),
      ],
    }),
    referrerReferralCode: new FormControl('', {
      nonNullable: true,
    }),
  });

  protected googleTagService = inject(GoogleTagService);

  protected router = inject(Router);

  isSubmitting = signal(false);

  showPassword = false;

  public changeToSignInPage = output<void>();

  public cacheService = inject(CacheService);

  public isRewardPointsEnabled = computed(() => {
    return this.cacheService.cachedContent()?.cache.config.rewardPointsMasterEnabled;
  });

  public signUpRequestCreated = output<{
    email: string;
    signupRequestId: string;
    otpRegenerationAfter: Date | null;
  }>();

  // Destroy subject for memory leak prevention
  private readonly destroy$ = new Subject<void>();

  protected registrationService = inject(RegistrationService);

  public serverValidationErrors = signal({
    email: '',
    password: '',
    referrerReferralCode: '',
    signupForm: '',
  });

  // Centralized validation rules object
  public fieldValidationRules = computed<FieldValidationRules>(() => {
    const serverValidationErrors = this.serverValidationErrors();
    return {
      email: {
        required: 'authentication.email_required',
        pattern: 'authentication.pattern_error',
        serverValidation: serverValidationErrors.email,
      },
      password: {
        required: 'authentication.password_required',
        minLength: 'authentication.password_min_length',
        pattern: 'authentication.password_pattern',
        serverValidation: serverValidationErrors.password,
      },
      referrerReferralCode: {
        pattern: 'authentication.referral_code_pattern',
        invalidReferralCode: 'authentication.referral_code_invalid',
        serverValidation: serverValidationErrors.referrerReferralCode,
      },
      signupForm: {
        serverValidation: serverValidationErrors.signupForm,
      },
    };
  });

  handleSignupSubmission(): Observable<boolean> {
    if (this.isSubmitting()) {
      return of(false);
    }

    this.isSubmitting.set(true);
    return this.registrationService.generateSignUpRequest(this.signupForm.getRawValue()).pipe(
      map(({ signupRequestId, otpNextGenerationAt }) => {
        this.signUpRequestCreated.emit({
          signupRequestId,
          otpRegenerationAfter: otpExpiryCalculator(otpNextGenerationAt),
          email: this.signupForm.getRawValue().email,
        });

        this.googleTagService.pushEvent('sign_up', {
          method: 'email',
          pageUrl: this.router.url,
          userStatus: 'registration_started',
        });

        return true;
      }),
      catchError((err) => {
        if (err instanceof HttpErrorResponse) {
          this.mapErrors(err);
        }

        return throwError(() => err);
      }),
      finalize(() => this.isSubmitting.set(false))
    );
  }

  // Check if password meets requirements
  getPasswordValidationStatus(): { minLength: boolean; hasNumber: boolean; hasSpecial: boolean } {
    const passwordValue = this.signupForm.get('password')?.value || '';
    return {
      minLength: passwordValue.length >= 8,
      hasNumber: /\d/.test(passwordValue),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(passwordValue),
    };
  }

  // Helper method to get first error message from centralized validation rules
  getFieldErrorMessage(fieldName: keyof FieldValidationRules): string {
    const formControl = this.signupForm.get(fieldName);
    if (!formControl?.errors) return '';

    // Check for server validation error first
    if (formControl.hasError('serverValidation')) {
      return formControl.getError('serverMessage') || '';
    }

    const fieldRules = this.fieldValidationRules()[fieldName];
    if (!fieldRules) return '';

    for (const [rule, message] of Object.entries(fieldRules)) {
      if (formControl.hasError(rule)) {
        return message;
      }
    }

    return '';
  }

  public mapErrors(signupError: HttpErrorResponse): void {
    // Handle server-side validation errors

    // Handle specific error responses
    // Check for referral code error using original message (before translation)
    const originalMessage = signupError.error?.originalMessage || signupError.error?.message;
    const translatedMessage = signupError.error?.message;
    const isReferralCodeError = originalMessage === 'Enter a correct Referral Code';

    if (isReferralCodeError) {
      // Set referral code error as serverValidation so it uses the translated message
      this.signupForm.controls.referrerReferralCode.addValidators([
        BackendValidationError(ErrorKeys.ServerValidation),
      ]);
      this.serverValidationErrors.update((errors) => ({
        ...errors,
        referrerReferralCode: translatedMessage,
      }));
      this.signupForm.controls.referrerReferralCode.updateValueAndValidity();
      return;
    }

    if (typeof signupError.error?.message === 'string') {
      this.signupForm.addValidators([BackendValidationError(ErrorKeys.ServerValidation)]);
      this.serverValidationErrors.update((errors) => ({
        ...errors,
        signupForm: signupError.error?.message,
      }));
      this.signupForm.updateValueAndValidity();
    }
  }
}
